// src/App.tsx — FIX safe-area + Telegram top controls overlap
import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Header } from "./components/layout/Header";
import { NavTabs, TabKey } from "./components/layout/NavTabs";
import { useTelegramAuth } from "./hooks/useTelegramAuth";
import { BrowserAuthPage, AdminLoginResponseDTO } from "./pages/BrowserAuthPage";
import { useUserStore } from "@/store/userStore";
import { useAdminNotificationsSocket } from "@/hooks/useAdminNotificationsSocket";

// ✅ TOASTS (глобальный контейнер)
import { ToastHost } from "@/components/ui/ToastHost";

// Тяжёлые страницы админ-панели грузим лениво — это резко ускоряет
// первый paint и время до показа BrowserAuthPage на медленных WebView.
const ReservationsPage = lazy(() =>
    import("./pages/ReservationsPage").then((m) => ({ default: m.ReservationsPage }))
);
const SubscriptionsPage = lazy(() =>
    import("./pages/SubscriptionsPage").then((m) => ({ default: m.SubscriptionsPage }))
);
const SettingsPage = lazy(() =>
    import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage }))
);
const LoadSchedulePage = lazy(() =>
    import("@/pages/LoadSchedulePage").then((m) => ({ default: m.LoadSchedulePage }))
);
const CourtsPage = lazy(() =>
    import("@/pages/CourtsPage").then((m) => ({ default: m.CourtsPage }))
);

const PageFallback: React.FC = () => (
    <div className="min-h-[40vh] flex items-center justify-center text-slate-400 text-sm">
        Загрузка раздела...
    </div>
);

type Env = "loading" | "telegram" | "browser";
const API_BASE_URL = "/api";

declare global {
    interface Window {
        Telegram?: any;
    }
}

/** =========================
 *  SERVER DEBUG LOGGER
 *  ========================= */

type DebugPayload = {
    sid: string;
    event: string;
    ts: string;
    data?: any;
};

function safeJson(value: any) {
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return String(value);
    }
}

function createSessionId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getTelegramSnapshot() {
    try {
        const tg = window.Telegram?.WebApp;
        const initUser = tg?.initDataUnsafe?.user;
        return {
            hasWebApp: !!tg,
            platform: tg?.platform ?? null,
            version: tg?.version ?? null,
            colorScheme: tg?.colorScheme ?? null,
            user: initUser
                ? {
                    id: initUser.id ?? null,
                    username: initUser.username ?? null,
                    first_name: initUser.first_name ?? null,
                    last_name: initUser.last_name ?? null,
                    language_code: initUser.language_code ?? null,
                }
                : null,
            initDataLength: typeof tg?.initData === "string" ? tg.initData.length : null,
            hashHasTgWebAppData: (window.location.hash || "").includes("tgWebAppData="),
            // useful on iOS
            safeAreaInsetTop: tg?.safeAreaInset?.top ?? null,
            safeAreaInsetBottom: tg?.safeAreaInset?.bottom ?? null,
            contentSafeAreaInsetTop: tg?.contentSafeAreaInset?.top ?? null,
            contentSafeAreaInsetBottom: tg?.contentSafeAreaInset?.bottom ?? null,
        };
    } catch (e) {
        return { error: String(e) };
    }
}

function detectTelegramEnv() {
    const tg = window.Telegram?.WebApp;
    const hash = window.location.hash || "";

    const hasHashInitData = hash.includes("tgWebAppData=");
    const hasRealInitData = typeof tg?.initData === "string" && tg.initData.length > 0;

    // ВАЖНО: просто наличие tg объекта НЕ означает, что мы внутри Telegram
    return hasRealInitData || hasHashInitData;
}

function useServerDebugLogger() {
    const sidRef = useRef<string>(createSessionId());
    const queueRef = useRef<DebugPayload[]>([]);
    const flushTimerRef = useRef<number | null>(null);
    const lastSentAtRef = useRef<number>(0);

    const flush = async () => {
        flushTimerRef.current = null;

        const batch = queueRef.current.splice(0, 20);
        if (batch.length === 0) return;

        try {
            await fetch(`${API_BASE_URL}/debug/log`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Debug-Sid": sidRef.current,
                },
                body: JSON.stringify({ sid: sidRef.current, events: batch }),
            });
            lastSentAtRef.current = Date.now();
        } catch {
            // ignore
        }
    };

    const scheduleFlush = () => {
        if (flushTimerRef.current != null) return;
        flushTimerRef.current = window.setTimeout(() => void flush(), 350);
    };

    const log = (event: string, data?: any) => {
        const payload: DebugPayload = {
            sid: sidRef.current,
            event,
            ts: new Date().toISOString(),
            data: safeJson(data),
        };

        queueRef.current.push(payload);

        const now = Date.now();
        const dt = now - lastSentAtRef.current;
        if (dt > 600) scheduleFlush();
        else scheduleFlush();
    };

    useEffect(() => {
        const onUnload = () => {
            try {
                const events = queueRef.current.slice(0, 50);
                if (events.length === 0) return;

                navigator.sendBeacon?.(
                    `${API_BASE_URL}/debug/log`,
                    new Blob([JSON.stringify({ sid: sidRef.current, events })], { type: "application/json" })
                );
            } catch {}
        };

        window.addEventListener("beforeunload", onUnload);
        return () => window.removeEventListener("beforeunload", onUnload);
    }, []);

    return { log, sid: sidRef.current };
}

/** =========================
 *  Telegram safe-area helpers
 *  ========================= */

function applyTelegramSafeAreaCssVars() {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    const top = tg?.contentSafeAreaInset?.top ?? tg?.safeAreaInset?.top ?? 0;
    const bottom = tg?.contentSafeAreaInset?.bottom ?? tg?.safeAreaInset?.bottom ?? 0;

    document.documentElement.style.setProperty("--tg-safe-area-inset-top", `${top}px`);
    document.documentElement.style.setProperty("--tg-safe-area-inset-bottom", `${bottom}px`);
}

function useTelegramSafeArea(env: Env) {
    useEffect(() => {
        if (env !== "telegram") return;

        const tg = window.Telegram?.WebApp;
        if (!tg) return;

        applyTelegramSafeAreaCssVars();

        try {
            tg.ready?.();
            tg.expand?.();
            tg.setHeaderColor?.("#020617"); // slate-950
            tg.setBackgroundColor?.("#020617");
            tg.enableClosingConfirmation?.(); // optional
        } catch {}

        const onViewportChanged = () => applyTelegramSafeAreaCssVars();
        try {
            tg.onEvent?.("viewportChanged", onViewportChanged);
        } catch {}

        window.addEventListener("resize", onViewportChanged);

        return () => {
            window.removeEventListener("resize", onViewportChanged);
            try {
                tg.offEvent?.("viewportChanged", onViewportChanged);
            } catch {}
        };
    }, [env]);
}

/** =========================
 *  App
 *  ========================= */

const TENNIS_CLUB_STAFF_APP_TITLE = "Заявки и расписание — панель клуба";

const App: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabKey>("reservations");
    const [env, setEnv] = useState<Env>("loading");

    useEffect(() => {
        document.title = TENNIS_CLUB_STAFF_APP_TITLE;
    }, []);

    const admin = useUserStore((s) => s.admin);
    const isReady = useUserStore((s) => s.isReady);
    const setBrowserAdmin = useUserStore((s) => s.setBrowserAdmin);
    const setTelegramAdmin = useUserStore((s) => s.setTelegramAdmin);

    const locationId = useUserStore((s) => s.locationId);
    const adminBotId = useUserStore((s) => s.adminBotId);
    const clientBotId = useUserStore((s) => s.clientBotId);

    const safeAreaStyle: React.CSSProperties = {
        paddingTop: "calc(var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) + 4px)",
        paddingBottom: "calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 16px)",
    };

    const { log: dbg, sid } = useServerDebugLogger();

    // ✅ hooks always
    useAdminNotificationsSocket(adminBotId ?? null, true);

    const tgAuth = useTelegramAuth();

    const tgAuthState = env === "telegram" ? tgAuth.state : ("loading" as const);
    const telegramUser = env === "telegram" ? tgAuth.user : null;
    const telegramAdmin = env === "telegram" ? tgAuth.admin : null;

    useTelegramSafeArea(env);

    useEffect(() => {
        const hasTelegram = detectTelegramEnv();

        dbg("APP_INIT", {
            sid,
            hasTelegram,
            origin: window.location.origin,
            href: window.location.href,
            ua: navigator.userAgent,
            tg: getTelegramSnapshot(),
        });

        setEnv(hasTelegram ? "telegram" : "browser");
        dbg("ENV_SET", { env: hasTelegram ? "telegram" : "browser" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dbg("STATE_SNAPSHOT", {
            env,
            tgAuthState,
            hasAdminInStore: !!admin,
            isReady,
            store: { locationId, adminBotId, clientBotId },
            tgAuth: {
                hasUser: !!telegramUser,
                telegramUserId: telegramUser?.telegramUserId ?? null,
                hasAdminFromAuth: !!telegramAdmin,
                adminLocationIdFromAuth: telegramAdmin?.locationId ?? null,
            },
            tg: getTelegramSnapshot(),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [env, tgAuthState, !!admin, isReady, locationId, adminBotId, clientBotId, telegramUser, telegramAdmin]);

    useEffect(() => {
        if (env === "browser") {
            dbg("RENDER_BROWSER_MODE", { hasAdmin: !!admin, tg: getTelegramSnapshot() });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [env, !!admin]);

    useEffect(() => {
        dbg("TG_ADMIN_APPLY_ENTER", {
            env,
            tgAuthState,
            hasAdminInStore: !!admin,
            hasAdminFromAuth: !!telegramAdmin,
            telegramUserId: telegramUser?.telegramUserId ?? null,
        });

        if (env !== "telegram") {
            dbg("TG_ADMIN_APPLY_SKIP", { reason: "NOT_TELEGRAM_ENV", env });
            return;
        }

        if (tgAuthState === "loading") {
            dbg("TG_ADMIN_APPLY_SKIP", { reason: "AUTH_LOADING" });
            return;
        }

        if (tgAuthState === "error") {
            dbg("TG_ADMIN_APPLY_SKIP", { reason: "AUTH_ERROR" });
            return;
        }

        if (tgAuthState === "forbidden") {
            dbg("TG_ADMIN_APPLY_SKIP", { reason: "AUTH_FORBIDDEN" });
            return;
        }

        if (!telegramAdmin) {
            dbg("TG_ADMIN_APPLY_NO_ADMIN_IN_RESPONSE", {
                telegramUserId: telegramUser?.telegramUserId ?? null,
            });
            return;
        }

        if (admin) {
            dbg("TG_ADMIN_APPLY_SKIP", { reason: "ADMIN_ALREADY_SET" });
            return;
        }

        setTelegramAdmin(telegramAdmin);
        dbg("STORE_SET_TELEGRAM_ADMIN_FROM_AUTH", {
            telegramUserId: telegramUser?.telegramUserId ?? null,
            locationId: telegramAdmin.locationId ?? null,
            adminBotId: telegramAdmin.adminBotId ?? null,
            clientBotId: telegramAdmin.clientBotId ?? null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [env, tgAuthState, admin, setTelegramAdmin, telegramAdmin, telegramUser]);

    /** =========================
     *  UI
     *  ========================= */

    if (env === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-50">
                <p className="text-sm">Загрузка...</p>
                <ToastHost />
            </div>
        );
    }

    if (!isReady) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-50">
                <p className="text-sm">Загрузка профиля...</p>
                <ToastHost />
            </div>
        );
    }

    // ===== Browser mode =====
    if (env === "browser") {
        if (!admin) {
            return (
                <>
                    <BrowserAuthPage onLoginSuccess={(dto: AdminLoginResponseDTO) => setBrowserAdmin(dto)} />
                    <ToastHost />
                </>
            );
        }

        return (
            <div className="min-h-screen bg-slate-950 text-slate-50" style={safeAreaStyle}>
                <div className="mx-auto max-w-3xl px-3 pb-6">
                    <Header
                        user={null}
                        goToReservations={() => setActiveTab("reservations")}
                        goToSettings={() => setActiveTab("settings")}
                        showLogout={true}
                        onLogout={() => {
                            useUserStore.getState().clear();
                            setActiveTab("reservations");
                        }}
                    />

                    <NavTabs active={activeTab} onChange={setActiveTab} />

                    <main className="mt-4">
                        <Suspense fallback={<PageFallback />}>
                            {activeTab === "reservations" && <ReservationsPage />}

                            {activeTab === "load" &&
                                (locationId && adminBotId ? (
                                    <LoadSchedulePage locationId={locationId} botId={adminBotId} />
                                ) : (
                                    <MissingLoadContextBlock locationId={locationId} adminBotId={adminBotId} />
                                ))}

                            {activeTab === "subscriptions" && <SubscriptionsPage apiBaseUrl="/api" />}

                            {activeTab === "settings" &&
                                (locationId && adminBotId ? (
                                    <SettingsPage locationId={locationId} adminBotId={adminBotId} />
                                ) : (
                                    <MissingSettingsContextBlock locationId={locationId} adminBotId={adminBotId} />
                                ))}

                            {activeTab === ("courts" as TabKey) &&
                                (locationId ? <CourtsPage locationId={locationId} /> : <MissingLocationBlock />)}
                        </Suspense>
                    </main>
                </div>

                {/* ✅ глобальные тосты поверх всего */}
                <ToastHost />
            </div>
        );
    }

    // ===== Telegram mode =====
    if (tgAuthState === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-50">
                <p className="text-sm">Проверяем доступ через Telegram...</p>
                <ToastHost />
            </div>
        );
    }

    const noAdminEvenThoughAuthorized = tgAuthState === "authorized" && !telegramAdmin;

    if (tgAuthState === "forbidden" || noAdminEvenThoughAuthorized) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-slate-50 px-4">
                <p className="text-sm text-center mb-2">
                    Доступ к этой панели есть только у администраторов теннисного клуба.
                </p>
                <p className="text-xs text-slate-400 text-center">
                    Если вы администратор, попросите владельца бота добавить ваш Telegram-аккаунт в список администраторов.
                </p>
                <ToastHost />
            </div>
        );
    }

    if (tgAuthState === "error") {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-slate-50 px-4">
                <p className="text-sm text-center mb-2">Ошибка авторизации через Telegram.</p>
                <p className="text-xs text-slate-400 text-center">
                    Попробуйте закрыть и заново открыть мини-приложение из Telegram.
                </p>
                <ToastHost />
            </div>
        );
    }

    if (!admin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-50">
                <p className="text-sm">Загружаем профиль администратора...</p>
                <ToastHost />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-50" style={safeAreaStyle}>
            <div className="mx-auto max-w-3xl px-3 pb-6">
                <Header
                    user={telegramUser as any}
                    goToReservations={() => setActiveTab("reservations")}
                    goToSettings={() => setActiveTab("settings")}
                />
                <NavTabs active={activeTab} onChange={setActiveTab} />

                <main className="mt-4">
                    <Suspense fallback={<PageFallback />}>
                        {activeTab === "reservations" && <ReservationsPage />}

                        {activeTab === "load" &&
                            (locationId && adminBotId ? (
                                <LoadSchedulePage locationId={locationId} botId={adminBotId} />
                            ) : (
                                <MissingLoadContextBlock locationId={locationId} adminBotId={adminBotId} />
                            ))}
                        {activeTab === "subscriptions" && <SubscriptionsPage apiBaseUrl="/api" />}

                        {activeTab === "settings" &&
                            (locationId && adminBotId ? (
                                <SettingsPage locationId={locationId} adminBotId={adminBotId} />
                            ) : (
                                <MissingSettingsContextBlock locationId={locationId} adminBotId={adminBotId} />
                            ))}
                    </Suspense>
                </main>

                {/* <div className="mt-6 text-[10px] text-slate-600">debug sid: {sid}</div> */}
            </div>

            {/* ✅ глобальные тосты поверх всего */}
            <ToastHost />
        </div>
    );
};

export default App;

/** =========================
 *  Small UI blocks
 *  ========================= */

const MissingLocationBlock: React.FC = () => (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-xs text-slate-300">
        Не удалось определить локацию администратора (locationId).
    </div>
);

const MissingSettingsContextBlock: React.FC<{ locationId: number | null; adminBotId: number | null }> = ({
                                                                                                             locationId,
                                                                                                             adminBotId,
                                                                                                         }) => (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-xs text-slate-300">
        Не хватает данных для страницы настроек. <br />
        locationId: <b>{String(locationId)}</b>, adminBotId: <b>{String(adminBotId)}</b>
    </div>
);

const MissingLoadContextBlock: React.FC<{ locationId: number | null; adminBotId: number | null }> = ({
                                                                                                         locationId,
                                                                                                         adminBotId,
                                                                                                     }) => (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-xs text-slate-300">
        Не хватает данных для вкладки «Загрузка/Слоты». <br />
        locationId: <b>{String(locationId)}</b>, adminBotId (header botId): <b>{String(adminBotId)}</b>
    </div>
);
